/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import {
    Flex,
    Stack,
    Grid,
    GridItem,
    Container,
    Skeleton,
    Box,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import CartTitle from './cart-title'
import {FreeShippingProgressSkeleton} from './free-shipping-progress-bar'
import {useResolvedCountryISO} from '../../../contexts/globale'

const CartItemSkeleton = () => {
    return (
        <Stack spacing={4} layerStyle="card" boxShadow="none">
            <Flex width="full" gap={{base: '10px', md: '20px'}}>
                <Stack spacing={3} align="flex-start">
                    <Skeleton
                        width={{base: '140px', lg: '180px'}}
                        height={{base: '175px', lg: '225px'}}
                    />
                </Stack>
                <Stack gap="20px">
                    <Skeleton width="80px" height="20px" />
                    <Skeleton
                        width={{base: '180px', sm: '180px', md: '280px', lg: '280px'}}
                        height="15px"
                    />
                    <Skeleton width="70px" height="15px" />
                    <Skeleton width="70px" height="15px" />
                    <Skeleton width="35px" height="15px" />
                </Stack>
            </Flex>
        </Stack>
    )
}

const OrderSummaryDetailsSkeleton = () => (
    <>
        <Skeleton width="190px" height="36px" />
        <Stack gap="15px">
            {[0, 1, 2, 3].map((key) => (
                <Flex key={key} justifyContent="space-between">
                    <Skeleton width="90px" height="16px" />
                    <Skeleton width="70px" height="16px" />
                </Flex>
            ))}
        </Stack>
        <Skeleton height="44px" borderRadius="3px" />
    </>
)

const CartSkeleton = () => {
    const cartStyles = useMultiStyleConfig('CartStyles')
    const resolvedCountryISO = useResolvedCountryISO()
    return (
        <Box backgroundColor="white" flex="1" data-testid="sf-cart-skeleton-container">
            <Container
                background="white"
                data-testid="sf-cart-skeleton"
                className="cart-container"
                {...cartStyles.cartContainer}
                backgroundColor="white"
            >
                <Stack
                    display={{base: 'flex', lg: 'none'}}
                    backgroundColor="gray.100"
                    margin="0 -15px"
                    padding="10px 15px 15px"
                    gap="15px"
                >
                    <Skeleton height="56px" borderRadius="3px" />
                    <Flex justifyContent="space-between" alignItems="center">
                        <Skeleton width="120px" height="20px" />
                        <Skeleton width="70px" height="20px" />
                    </Flex>
                </Stack>
                {resolvedCountryISO === 'US' && (
                    <Box display={{base: 'block', lg: 'none'}}>
                        <FreeShippingProgressSkeleton />
                    </Box>
                )}
                <Grid
                    templateColumns={{
                        base: '1fr',
                        lg: 'minmax(520px, 800px) 394px',
                        '2lg': 'minmax(540px, 800px) 414px'
                    }}
                    gap={{base: 10, lg: '20px', '3lg': '66px'}}
                    justifyContent="space-between"
                >
                    <GridItem display="grid" gap="30px" alignContent="start">
                        <Stack gap="30px">
                            <CartTitle />
                            <CartItemSkeleton />
                            <CartItemSkeleton />
                        </Stack>
                    </GridItem>
                    <GridItem display="grid" gap="30px" alignContent="start">
                        <Stack
                            spacing={4}
                            backgroundColor="gray.100"
                            padding="15px"
                            display={{base: 'none', lg: 'flex'}}
                        >
                            <Skeleton height="56px" borderRadius="3px" />
                            <OrderSummaryDetailsSkeleton />
                        </Stack>
                        <Skeleton aspectRatio="23 / 6" borderRadius="3px" />
                        {resolvedCountryISO === 'US' && (
                            <Skeleton height="70px" borderRadius="3px" />
                        )}
                        <Stack gap="15px">
                            <Skeleton width="190px" height="36px" />
                            <Skeleton height="46px" borderRadius="10px" />
                            <Skeleton height="64px" borderRadius="10px" />
                        </Stack>
                        <Stack
                            spacing={4}
                            backgroundColor="gray.100"
                            padding="15px"
                            margin="0 -15px -30px"
                            display={{base: 'flex', lg: 'none'}}
                        >
                            <OrderSummaryDetailsSkeleton />
                        </Stack>
                        <Skeleton height={{base: '130px', lg: '56px'}} borderRadius="3px" />
                    </GridItem>
                </Grid>
            </Container>
        </Box>
    )
}

export default CartSkeleton
